import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Clients from "@/pages/Clients";
import Rentals from "@/pages/Rentals";
import Team from "@/pages/Team";
import MyRentals from "@/pages/MyRentals";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <Loader2 className="h-8 w-8 animate-spin text-[#0A2540]" />
  </div>
);

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "client" ? "/my-rentals" : "/dashboard"} replace />;
  }
  return <Layout>{children}</Layout>;
}

function LandingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={user.role === "client" ? "/my-rentals" : "/dashboard"} replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/dashboard" element={<Protected roles={["admin", "staff"]}><Dashboard /></Protected>} />
      <Route path="/inventory" element={<Protected roles={["admin", "staff"]}><Inventory /></Protected>} />
      <Route path="/clients" element={<Protected roles={["admin", "staff"]}><Clients /></Protected>} />
      <Route path="/rentals" element={<Protected roles={["admin", "staff"]}><Rentals /></Protected>} />
      <Route path="/team" element={<Protected roles={["admin"]}><Team /></Protected>} />
      <Route path="/my-rentals" element={<Protected roles={["client", "admin", "staff"]}><MyRentals /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
